import { ICreateOAuthClientRequest } from '../../../../queries';

export const MAX_REDIRECT_URIS = 5;
const MAX_NAME_LENGTH = 50;
const MAX_DESCRIPTION_LENGTH = 300;

const NAME_PATTERN = /^[A-Za-z0-9 _',./():&-]+$/;
const NON_ALPHANUMERIC = /[^a-z0-9]/g;
const RESERVED_NAME_TERMS = ['levitate', 'realmagic', 'coffee'];

const isHttpsAbsoluteUri = (value: string): boolean => {
	try {
		const url = new URL(value);
		return url.protocol === 'https:' && !!url.hostname && value.toLowerCase().startsWith('https://');
	} catch {
		return false;
	}
};

/**
 * Client-side mirror of the API's CreateOAuthClientRequest.Validate(). Returns the first error
 * message, or null when valid. The server re-validates — this is for fast feedback only.
 */
export const validateCreateOAuthClient = (request: ICreateOAuthClientRequest): string | null => {
	const name = request.name?.trim() ?? '';
	if (!name) {
		return 'Name is required.';
	}
	if (name.length > MAX_NAME_LENGTH) {
		return 'Name must be 50 characters or fewer.';
	}
	if (!NAME_PATTERN.test(name)) {
		return "Name may only contain letters, numbers, spaces, and the characters _ - ' , . / ( ) : &";
	}
	const normalizedName = name.toLowerCase().replace(NON_ALPHANUMERIC, '');
	if (RESERVED_NAME_TERMS.some(term => normalizedName.includes(term))) {
		return 'Name may not reference Levitate, Coffee, or Real Magic.';
	}

	if (request.description && request.description.length > MAX_DESCRIPTION_LENGTH) {
		return 'Description must be 300 characters or fewer.';
	}

	const redirectUris = request.redirectUris ?? [];
	if (redirectUris.length < 1) {
		return 'At least one redirect URI is required.';
	}
	if (redirectUris.length > MAX_REDIRECT_URIS) {
		return `No more than ${MAX_REDIRECT_URIS} redirect URIs are allowed.`;
	}
	if (new Set(redirectUris).size !== redirectUris.length) {
		return 'Redirect URIs must be unique.';
	}
	for (const redirectUri of redirectUris) {
		if (!isHttpsAbsoluteUri(redirectUri)) {
			return `Redirect URI '${redirectUri}' must be a fully-qualified, absolute HTTPS URI.`;
		}
	}

	const scopes = request.scopes ?? [];
	if (scopes.length < 1) {
		return 'At least one scope is required.';
	}

	return null;
};
